import Dexie from 'dexie';
import { SHARED_PUSH_TABLES, type OutboxOp, type PushedOp, type Row, type SyncCursor, type SyncStore, type SyncTable } from '@pos/sync';
import type { LocalDb } from './db.ts';
import { undoLocalOp } from './outbox.ts';
import type { SyncRejectionRow } from './types.ts';

const cursorKey = (businessId: string, table: SyncTable) => `${businessId}:${table}`;

/** Implementación de SyncStore sobre Dexie. */
export class DexieSyncStore implements SyncStore {
  constructor(private readonly db: LocalDb) {}

  async nextPendingOps(authorId: string, limit: number, now: number): Promise<OutboxOp[]> {
    const own = await this.db.outbox
      .where('[status+created_by+seq]')
      .between(['pending', authorId, Dexie.minKey], ['pending', authorId, Dexie.maxKey])
      .limit(limit)
      .toArray();
    const shared = await this.db.outbox
      .where('[table+status]')
      .anyOf(SHARED_PUSH_TABLES.map((t) => [t, 'pending']))
      .filter((op) => op.created_by !== authorId)
      .limit(limit)
      .toArray();
    const ops = [...own, ...shared].sort((a, b) => a.seq! - b.seq!).slice(0, limit);
    const ready: OutboxOp[] = [];
    // Si la primera está esperando su reintento, las siguientes esperan también (orden FIFO).
    for (const op of ops) {
      if (op.next_attempt_at > now) break;
      ready.push(op);
    }
    return ready;
  }

  async markPushed(pushed: PushedOp[]): Promise<void> {
    if (pushed.length === 0) return;
    await this.db.transaction('rw', [this.db.outbox, this.db.stock_movements, this.db.sales, this.db.account_entries, this.db.cash_movements], async () => {
      await this.db.outbox.bulkDelete(pushed.map((p) => p.op.seq!));
      // Los movimientos guardan la transacción del servidor que los aplicó: así se sabe si
      // el resumen de stock que se baje después ya los incluye (ver computeStock).
      for (const { op, xid } of pushed) {
        if (op.table === 'stock_movements' && op.kind === 'insert' && xid) {
          await this.db.stock_movements.update(op.row_id, { server_xid: xid });
        }
        // Una venta genera sus movimientos en el servidor, con los mismos ids que los locales.
        if (op.table === 'sales' && op.kind === 'insert' && xid) {
          await this.db.sales.update(op.row_id, { server_xid: xid });
          for (const item of (op.payload.items as Array<{ movement_id: string }> | undefined) ?? []) {
            await this.db.stock_movements.update(item.movement_id, { server_xid: xid });
          }
          // Los pagos a cuenta corriente generan su movimiento con el id del pago.
          for (const p of (op.payload.payments as Array<{ id: string; method: string }> | undefined) ?? []) {
            if (p.method === 'account') await this.db.account_entries.update(p.id, { server_xid: xid });
          }
        }
        if ((op.table === 'account_entries' || op.table === 'cash_movements') && op.kind === 'insert' && xid) {
          await this.db.table(op.table).update(op.row_id, { server_xid: xid });
        }
      }
    });
  }

  async markRetry(seq: number, error: string, nextAttemptAt: number): Promise<void> {
    await this.db.outbox
      .where('seq')
      .equals(seq)
      .modify((op) => {
        op.attempts += 1;
        op.last_error = error;
        op.next_attempt_at = nextAttemptAt;
      });
  }

  async markRejected(seq: number, error: string): Promise<void> {
    await this.db.outbox.update(seq, { status: 'rejected', last_error: error });
  }

  async countOps(): Promise<{ pending: number; rejected: number }> {
    const [pending, rejected] = await Promise.all([
      this.db.outbox.where('status').equals('pending').count(),
      this.db.outbox.where('status').equals('rejected').count(),
    ]);
    return { pending, rejected };
  }

  async getCursor(businessId: string, table: SyncTable): Promise<SyncCursor | null> {
    const row = await this.db.cursors.get(cursorKey(businessId, table));
    return row ? { xid: row.xid, id: row.id } : null;
  }

  async applyPulled(businessId: string, table: SyncTable, rows: Row[], cursor: SyncCursor): Promise<void> {
    await this.db.transaction('rw', [this.db.syncTable(table), this.db.outbox, this.db.cursors], async () => {
      await this.putRebased(table, rows);
      await this.db.cursors.put({ key: cursorKey(businessId, table), ...cursor });
    });
    if (table === 'sync_rejections') await this.reconcileQuarantine(rows as unknown as SyncRejectionRow[]);
  }

  /**
   * Cambios de este dispositivo que estaban en la cuarentena del servidor y ya se resolvieron:
   * si el dueño los aplicó, salen de la cola como si se hubieran enviado; si se descartaron,
   * se deshacen acá también.
   */
  private async reconcileQuarantine(rows: SyncRejectionRow[]): Promise<void> {
    const resolved = new Map(rows.filter((r) => r.status !== 'pending').map((r) => [r.id, r]));
    if (resolved.size === 0) return;
    const ops = await this.db.outbox.where('op_id').anyOf([...resolved.keys()]).toArray();
    const applied: PushedOp[] = [];
    for (const op of ops) {
      const row = resolved.get(op.op_id)!;
      if (row.status === 'applied') applied.push({ op, xid: row.applied_xid ?? undefined });
      else await undoLocalOp(this.db, op);
    }
    await this.markPushed(applied);
  }

  async applyRows(table: SyncTable, rows: Row[]): Promise<void> {
    await this.db.transaction('rw', [this.db.syncTable(table), this.db.outbox], () => this.putRebased(table, rows));
  }

  /**
   * Guarda la versión del servidor y vuelve a aplicar encima los cambios locales que
   * todavía no se enviaron, para que la pantalla no muestre valores viejos mientras tanto.
   */
  private async putRebased(table: SyncTable, rows: Row[]): Promise<void> {
    if (rows.length === 0) return;
    // Una sola consulta por página: los cambios pendientes de esta tabla, agrupados por fila.
    const pending = await this.db.outbox
      .where('[table+status]')
      .equals([table, 'pending'])
      .filter((op) => op.kind === 'patch')
      .sortBy('seq');
    const byRow = new Map<string, Record<string, unknown>[]>();
    for (const op of pending) {
      const list = byRow.get(op.row_id) ?? [];
      list.push(op.payload);
      byRow.set(op.row_id, list);
    }
    const merged = rows.map((row) => (byRow.get(row.id) ?? []).reduce<Row>((acc, p) => ({ ...acc, ...p }), row));
    await this.db.syncTable(table).bulkPut(merged);
  }
}
